// Sopwith 3D - mission (.sop) file loading.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// A mission file may carry up to four sections: `level` (terrain and
// objects), `symbols` (sprite replacements), `sounds` and `title`. The
// original game data ships as assets/levels/original.sop, so the sim has no
// built-in level; a custom mission that omits a section falls back to the
// original's.

import { MAX_Y, SCR_WDTH } from './constants';
import {
  Faction,
  FACTION_NAMES,
  OBJ_TYPE_NAMES,
  ObjType,
  Transform,
  TRANSFORM_NAMES,
  type LevelData,
  type OriginalOb,
} from './types';
import { parseYocton, yoctonEnum, yoctonInt, yoctonObject, YoctonError, type YNode } from './yocton';

export interface Mission {
  level: LevelData | null;
  /** Symbol set name -> ASCII-art frames. */
  symbols: Map<string, string[]> | null;
  /** Raw title section, kept for a later title-screen implementation. */
  title: YNode | null;
  /** Raw sounds section. */
  sounds: YNode | null;
}

function parseObject(node: YNode): OriginalOb {
  const ob: OriginalOb = {
    type: ObjType.GROUND,
    x: 0,
    orient: 0,
    territoryL: 0,
    territoryR: 0,
    faction: Faction.NONE,
    transform: Transform.NONE,
  };
  for (const p of node.props) {
    switch (p.name) {
      case 'x':
        ob.x = yoctonInt(p);
        break;
      case 'orient':
        ob.orient = yoctonInt(p);
        break;
      case 'territory_l':
        ob.territoryL = yoctonInt(p);
        break;
      case 'territory_r':
        ob.territoryR = yoctonInt(p);
        break;
      case 'transform':
        ob.transform = yoctonEnum(p, TRANSFORM_NAMES, true);
        break;
      case 'type':
        ob.type = yoctonEnum(p, OBJ_TYPE_NAMES);
        break;
      case 'faction':
      case 'owner':
        ob.faction = yoctonEnum(p, FACTION_NAMES);
        break;
      default:
        // Unknown properties are ignored, as in the original loader.
        break;
    }
  }
  return ob;
}

function parseGround(node: YNode): Uint8Array {
  const values: number[] = [];
  for (const p of node.props) {
    if (p.name !== '_') {
      throw new YoctonError(`expected prop name '_', got '${p.name}'`, p.line);
    }
    values.push(yoctonInt(p));
  }
  return Uint8Array.from(values);
}

function parseLevel(node: YNode): LevelData {
  const objects: OriginalOb[] = [];
  let ground: Uint8Array | null = null;
  for (const p of node.props) {
    if (p.name === 'object') {
      objects.push(parseObject(yoctonObject(p)));
    } else if (p.name === 'ground') {
      ground = parseGround(yoctonObject(p));
    }
  }
  if (!ground) {
    throw new Error('level has no ground section');
  }
  if (ground.length < SCR_WDTH) {
    throw new Error(`ground must have at least ${SCR_WDTH} values`);
  }
  for (let i = 0; i < ground.length; i++) {
    if (ground[i] >= MAX_Y) {
      throw new Error(`ground[${i}] = ${ground[i]} is out of range`);
    }
  }
  return { maxX: ground.length, ground, objects };
}

function parseSymbols(node: YNode): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const set of node.props) {
    const frames: string[] = [];
    for (const f of yoctonObject(set).props) {
      if (!/^\d+$/.test(f.name) || typeof f.value !== 'string') {
        throw new YoctonError(`symbol ${set.name}: expected numbered frames`, f.line);
      }
      frames[parseInt(f.name, 10)] = f.value;
    }
    out.set(set.name, frames);
  }
  return out;
}

/** Parse a .sop mission file. */
export function parseMission(text: string): Mission {
  const root = parseYocton(text);
  const m: Mission = { level: null, symbols: null, title: null, sounds: null };
  for (const p of root.props) {
    switch (p.name) {
      case 'level':
        if (m.level) {
          throw new YoctonError('only one level per file supported', p.line);
        }
        m.level = parseLevel(yoctonObject(p));
        break;
      case 'symbols':
        m.symbols = parseSymbols(yoctonObject(p));
        break;
      case 'title':
        m.title = yoctonObject(p);
        break;
      case 'sounds':
        m.sounds = yoctonObject(p);
        break;
      default:
        break;
    }
  }
  return m;
}
